import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../../components/ui/dialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '../../components/ui/alert-dialog';
import DataTable from '../../components/DataTable';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  ArrowRightLeft,
  Clock,
  CheckCircle,
  XCircle,
  User,
  DollarSign,
  MessageSquare,
  Eye,
  Check,
  X,
} from 'lucide-react';

const MovementRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Dialogs
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form data
  const [approveForm, setApproveForm] = useState({
    admin_notes: '',
    new_monthly_fee: '',
    effective_date: ''
  });
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/movement-requests');
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch movement requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const getFilteredRequests = () => {
    if (activeTab === 'all') return requests;
    return requests.filter(r => r.status === activeTab);
  };

  const getStatusCounts = () => {
    const counts = { all: requests.length, pending: 0, approved: 0, rejected: 0 };
    requests.forEach(r => {
      if (counts[r.status] !== undefined) {
        counts[r.status]++;
      }
    });
    return counts;
  };

  const openDetailsDialog = (request) => {
    setSelectedRequest(request);
    setDetailsDialog(true);
  };

  const openApproveDialog = (request) => {
    setSelectedRequest(request);
    setApproveForm({
      admin_notes: '',
      new_monthly_fee: request.new_monthly_fee || '',
      effective_date: new Date().toISOString().split('T')[0]
    });
    setApproveDialog(true);
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setRejectDialog(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await api.put(`/movement-requests/${selectedRequest.id}/approve`, {
        admin_notes: approveForm.admin_notes || null,
        new_monthly_fee: approveForm.new_monthly_fee ? parseFloat(approveForm.new_monthly_fee) : null,
        effective_date: approveForm.effective_date || null
      });
      toast.success('Movement request approved');
      setApproveDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      await api.put(`/movement-requests/${selectedRequest.id}/reject`, {
        admin_notes: rejectReason
      });
      toast.success('Movement request rejected');
      setRejectDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      key: 'player_name',
      label: 'Player',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div>
          <p className="font-medium text-slate-900">{row.player_name}</p>
          <p className="text-xs text-slate-500">{row.parent_name}</p>
        </div>
      )
    },
    {
      key: 'current',
      label: 'Current',
      render: (row) => (
        <div className="text-sm">
          <p className="font-medium">{row.current_program_name}</p>
          <p className="text-slate-500">{row.current_class_name || 'No class'}</p>
        </div>
      )
    },
    {
      key: 'new',
      label: 'Requested',
      render: (row) => (
        <div className="text-sm">
          <p className="font-medium text-blue-600">{row.new_program_name}</p>
          <p className="text-blue-500">{row.new_class_name}</p>
        </div>
      )
    },
    {
      key: 'fee_difference',
      label: 'Fee Change',
      sortable: true,
      render: (row) => {
        const diff = row.fee_difference;
        if (diff === null || diff === undefined) return <span className="text-slate-400">-</span>;
        return (
          <span className={`font-medium ${diff > 0 ? 'text-red-600' : diff < 0 ? 'text-green-600' : 'text-slate-600'}`}>
            {diff > 0 ? '+' : ''}{diff !== 0 ? `${cs()}${diff}` : 'No change'}
          </span>
        );
      }
    },
    {
      key: 'coach_name',
      label: 'Requested By',
      sortable: true,
      filterable: true,
      filterType: 'text'
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleDateString()
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const statusConfig = {
          pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700', icon: Clock },
          approved: { label: 'Approved', className: 'bg-green-100 text-green-700', icon: CheckCircle },
          rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700', icon: XCircle }
        };
        const config = statusConfig[row.status] || statusConfig.pending;
        const Icon = config.icon;
        return (
          <Badge className={`${config.className} flex items-center gap-1 w-fit`}>
            <Icon className="h-3 w-3" />
            {config.label}
          </Badge>
        );
      }
    }
  ];

  const actions = (row) => (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => openDetailsDialog(row)}
        data-testid={`view-request-${row.id}`}
      >
        <Eye className="h-4 w-4" />
      </Button>
      {row.status === 'pending' && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openApproveDialog(row)}
            className="text-green-600 border-green-200 hover:bg-green-50"
            data-testid={`approve-request-${row.id}`}
          >
            <Check className="h-4 w-4 mr-1" />
            Approve
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openRejectDialog(row)}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid={`reject-request-${row.id}`}
          >
            <X className="h-4 w-4 mr-1" />
            Reject
          </Button>
        </>
      )}
    </div>
  );

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <DashboardLayout title="Movement Requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Movement Requests">
      <div className="space-y-6" data-testid="movement-requests-page">
        <p className="text-slate-600">Review and manage player movement requests from coaches</p>

        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-slate-600" />
              Player Movement Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="flex w-full overflow-x-auto lg:w-auto lg:inline-flex mb-4">
                <TabsTrigger value="pending" className="flex items-center gap-2" data-testid="tab-pending">
                  <Clock className="h-4 w-4 text-amber-600" />
                  Pending ({statusCounts.pending})
                </TabsTrigger>
                <TabsTrigger value="approved" className="flex items-center gap-2" data-testid="tab-approved">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Approved ({statusCounts.approved})
                </TabsTrigger>
                <TabsTrigger value="rejected" className="flex items-center gap-2" data-testid="tab-rejected">
                  <XCircle className="h-4 w-4 text-red-600" />
                  Rejected ({statusCounts.rejected})
                </TabsTrigger>
                <TabsTrigger value="all" className="flex items-center gap-2" data-testid="tab-all">
                  All ({statusCounts.all})
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab}>
                <DataTable
                  data={getFilteredRequests()}
                  columns={columns}
                  actions={actions}
                  emptyIcon={ArrowRightLeft}
                  emptyMessage={activeTab === 'pending' ? "No pending movement requests." : `No ${activeTab} requests.`}
                  searchable={true}
                  searchFields={['player_name', 'parent_name', 'coach_name', 'current_program_name', 'new_program_name']}
                  testIdPrefix="movement-requests"
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Request Details Dialog */}
        <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-blue-500" />
                Movement Request Details
              </DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4 mt-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Status:</span>
                  <Badge className={
                    selectedRequest.status === 'approved' ? 'bg-green-100 text-green-700' :
                    selectedRequest.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }>
                    {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                  </Badge>
                </div>

                {/* Player Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="h-4 w-4 text-slate-500" />
                    <span className="font-medium">Player Information</span>
                  </div>
                  <p><strong>Player:</strong> {selectedRequest.player_name}</p>
                  <p><strong>Parent:</strong> {selectedRequest.parent_name}</p>
                  <p className="text-sm text-slate-500">{selectedRequest.parent_email}</p>
                </div>

                {/* Movement Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">FROM</p>
                    <p className="font-medium">{selectedRequest.current_program_name}</p>
                    <p className="text-sm text-slate-600">{selectedRequest.current_class_name || 'No class'}</p>
                    <p className="text-sm text-slate-500 mt-2">{cs()}{selectedRequest.current_monthly_fee}/mo</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <p className="text-xs text-blue-600 mb-1">TO</p>
                    <p className="font-medium text-blue-700">{selectedRequest.new_program_name}</p>
                    <p className="text-sm text-blue-600">{selectedRequest.new_class_name}</p>
                    <p className="text-sm text-blue-500 mt-2">{cs()}{selectedRequest.new_monthly_fee}/mo</p>
                  </div>
                </div>

                {/* Fee Change */}
                {selectedRequest.fee_difference !== null && selectedRequest.fee_difference !== 0 && (
                  <div className={`p-3 rounded-lg flex items-center gap-2 ${
                    selectedRequest.fee_difference > 0 ? 'bg-red-50' : 'bg-green-50'
                  }`}>
                    <DollarSign className={`h-5 w-5 ${selectedRequest.fee_difference > 0 ? 'text-red-600' : 'text-green-600'}`} />
                    <span className={`font-medium ${selectedRequest.fee_difference > 0 ? 'text-red-700' : 'text-green-700'}`}>
                      Monthly fee will {selectedRequest.fee_difference > 0 ? 'increase' : 'decrease'} by {cs()}{Math.abs(selectedRequest.fee_difference)}
                    </span>
                  </div>
                )}

                {/* Reason */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-4 w-4 text-slate-500" />
                    <span className="font-medium">Reason for Request</span>
                  </div>
                  <p className="text-sm">{selectedRequest.reason}</p>
                  {selectedRequest.coach_notes && (
                    <p className="text-sm text-slate-500 mt-2"><strong>Coach Notes:</strong> {selectedRequest.coach_notes}</p>
                  )}
                </div>

                {/* Request Info */}
                <div className="text-sm text-slate-500 space-y-1">
                  <p><strong>Requested by:</strong> {selectedRequest.coach_name}</p>
                  <p><strong>Date:</strong> {new Date(selectedRequest.created_at).toLocaleString()}</p>
                  {selectedRequest.reviewed_at && (
                    <>
                      <p><strong>Reviewed by:</strong> {selectedRequest.reviewer_name}</p>
                      <p><strong>Reviewed at:</strong> {new Date(selectedRequest.reviewed_at).toLocaleString()}</p>
                    </>
                  )}
                  {selectedRequest.admin_notes && (
                    <p><strong>Admin Notes:</strong> {selectedRequest.admin_notes}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Approve Dialog */}
        <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                Approve Movement Request
              </DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <p><strong>Player:</strong> {selectedRequest.player_name}</p>
                  <p><strong>Move to:</strong> {selectedRequest.new_program_name} / {selectedRequest.new_class_name}</p>
                </div>

                <div className="space-y-2">
                  <Label>New Monthly Fee (optional override)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={approveForm.new_monthly_fee}
                    onChange={(e) => setApproveForm({...approveForm, new_monthly_fee: e.target.value})}
                    placeholder={`Default: ${cs()}${selectedRequest.new_monthly_fee}`}
                    data-testid="approve-fee-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={approveForm.effective_date}
                    onChange={(e) => setApproveForm({...approveForm, effective_date: e.target.value})}
                    data-testid="approve-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Admin Notes (optional)</Label>
                  <Textarea
                    value={approveForm.admin_notes}
                    onChange={(e) => setApproveForm({...approveForm, admin_notes: e.target.value})}
                    placeholder="Any notes for the parent/coach..."
                    rows={2}
                    data-testid="approve-notes-input"
                  />
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setApproveDialog(false)} disabled={actionLoading}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleApprove} 
                    className="bg-green-600 hover:bg-green-700"
                    disabled={actionLoading}
                    data-testid="confirm-approve-btn"
                  >
                    {actionLoading ? 'Processing...' : 'Approve & Update Enrollment'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <AlertDialog open={rejectDialog} onOpenChange={setRejectDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Reject Movement Request
              </AlertDialogTitle>
              <AlertDialogDescription>
                {selectedRequest && (
                  <div className="space-y-3 mt-2">
                    <p>Rejecting request to move <strong>{selectedRequest.player_name}</strong> to {selectedRequest.new_program_name} / {selectedRequest.new_class_name}</p>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-700">Reason for Rejection *</Label>
                      <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Please provide a reason for rejecting this request..."
                        rows={3}
                        data-testid="reject-reason-input"
                      />
                    </div>
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleReject}
                className="bg-red-600 hover:bg-red-700"
                disabled={actionLoading || !rejectReason.trim()}
                data-testid="confirm-reject-btn"
              >
                {actionLoading ? 'Processing...' : 'Reject Request'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default MovementRequests;
